import { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, AreaChart, Area, Cell,
} from "recharts";
import { TrendingUp, Send, CheckCircle2, XCircle, BarChart3 } from "lucide-react";
import api from "@/lib/api";
import { Card, Spinner, Badge } from "@/components/kit";

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="flex justify-center py-20"><Spinner className="w-7 h-7" /></div>;

  const funnelData = Object.entries(data.funnel).map(([name, value]) => ({ name, value }));
  const colors = ["#94a3b8", "#0ea5e9", "#8b5cf6", "#f59e0b", "#10b981", "#f43f5e"];
  const respRate = data.blast.sent + data.blast.failed > 0
    ? Math.round((data.blast.sent / (data.blast.sent + data.blast.failed)) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-sky-50 grid place-items-center"><BarChart3 className="w-6 h-6 text-sky-600" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold">Analytics</h1>
          <p className="text-slate-500 mt-1">Performa scraping & blast kamu secara real-time.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: TrendingUp, label: "Total Leads", value: data.total_leads, color: "sky" },
          { icon: Send, label: "Pesan Terkirim", value: data.blast.sent, color: "green" },
          { icon: XCircle, label: "Pesan Gagal", value: data.blast.failed, color: "red" },
          { icon: CheckCircle2, label: "Delivery Rate", value: `${respRate}%`, color: "violet" },
        ].map((s) => (
          <Card key={s.label} data-testid={`stat-card-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="p-5">
            <s.icon className="w-6 h-6 text-sky-500 mb-3" />
            <p className="font-display text-3xl font-extrabold">{s.value}</p>
            <p className="text-sm text-slate-500 mt-1">{s.label}</p>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-display font-bold text-lg mb-4">Leads (14 hari)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.leads_series}>
              <defs>
                <linearGradient id="ga" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#0ea5e9" strokeWidth={2.5} fill="url(#ga)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-bold text-lg mb-4">Funnel Konversi</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={funnelData} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" hide allowDecimals={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} width={80} className="capitalize" />
              <Tooltip />
              <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                {funnelData.map((_, i) => <Cell key={i} fill={colors[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-display font-bold text-lg mb-4">Top Kota</h3>
          {data.top_cities.length === 0 ? <p className="text-sm text-slate-400">Belum ada data.</p> :
            data.top_cities.map((c, i) => (
              <div key={c.name} className="flex items-center gap-3 py-2">
                <Badge color="sky">{i + 1}</Badge>
                <span className="flex-1 text-sm text-slate-700">{c.name}</span>
                <div className="w-24 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-sky-500" style={{ width: `${(c.count / data.top_cities[0].count) * 100}%` }} />
                </div>
                <span className="text-sm font-semibold w-8 text-right">{c.count}</span>
              </div>
            ))}
        </Card>
        <Card className="p-6">
          <h3 className="font-display font-bold text-lg mb-4">Top Kategori</h3>
          {data.top_categories.length === 0 ? <p className="text-sm text-slate-400">Belum ada data.</p> :
            data.top_categories.map((c, i) => (
              <div key={c.name} className="flex items-center gap-3 py-2">
                <Badge color="violet">{i + 1}</Badge>
                <span className="flex-1 text-sm text-slate-700">{c.name}</span>
                <div className="w-24 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-violet-500" style={{ width: `${(c.count / data.top_categories[0].count) * 100}%` }} />
                </div>
                <span className="text-sm font-semibold w-8 text-right">{c.count}</span>
              </div>
            ))}
        </Card>
      </div>
    </div>
  );
}
